import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { BookOpen, FileText, CheckCircle2 } from "lucide-react";

import Modal from "../../../components/Modal";
import EntryReviewBox from "../components/EntryReviewBox";
import LogbookChat from "../components/LogbookChat";
import WeekDropdown from "../components/WeekDropdown";
import WeekCalendar from "../components/WeekCalendar";
import StatusBadge from "../components/StatusBadge";

import {
  useGetSupervisorLogbookDetailsQuery,
  useGetSupervisorLogbookWeekEntriesQuery,
  useApproveDailyLogMutation,
  useApproveLogbookWeekMutation,
  useGetLogbookWeeksStatusQuery,
  useSendLogbookForAssessmentMutation,
  useUnsubmitLogbookMutation,
} from "../api/logbookApi";

import { Day, DAYS, NUM_TO_DAY, safeDate } from "../utils/logbook";

/* ---------------- helpers ---------------- */

function safeMsg(x: any) {
  return String(
    x?.data?.message ||
      x?.message ||
      x?.error ||
      x?.data?.error ||
      "Something went wrong"
  );
}

// A dropped/aborted request (bad network) — NOT a 404/403 access problem.
// RTK reports these as FETCH_ERROR / TIMEOUT_ERROR, or PARSING_ERROR with an
// AbortError when the fetch is cancelled mid-flight.
function isNetworkOrAbort(err: any): boolean {
  if (!err) return false;
  const status = err?.status ?? err?.error?.status;
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") return true;
  if (status === "PARSING_ERROR") return true; // aborted/interrupted (originalStatus 200, empty body)
  const msg = String(err?.error ?? err?.data?.error ?? "");
  return /abort|network|failed to fetch/i.test(msg);
}

// normalize week entries response to always be an array
function normalizeEntries(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.entries)) return res.entries;
  if (Array.isArray(res?.data?.entries)) return res.data.entries;
  if (Array.isArray(res?.data?.data?.entries)) return res.data.data.entries;
  return [];
}

function personNameFromApplication(app: any) {
  if (!app) return "—";
  return [app?.title, app?.surname, app?.other_names || app?.othernames]
    .filter(Boolean)
    .join(" ");
}

/* ---------------- component ---------------- */

export default function SupervisorLogbookReview() {
  const navigate = useNavigate();
  const { logbookId } = useParams<{ logbookId: string }>();

  const [selectedWeek, setSelectedWeek] = useState<number>(1);

  // info modal
  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  // confirm approve
  const [approveConfirm, setApproveConfirm] = useState<{
    open: boolean;
    entry?: any;
    day?: Day;
  }>({ open: false });

  // confirm submit assessment
  const [assessmentConfirmOpen, setAssessmentConfirmOpen] = useState(false);
  const [weekConfirmOpen, setWeekConfirmOpen] = useState(false);

  /* ---------------- queries ---------------- */

  const {
    data: logbook,
    isLoading: lbLoading,
    error: lbError,
    refetch: refetchLogbook,
  } = useGetSupervisorLogbookDetailsQuery(
    { logbook_id: logbookId || "" },
    { skip: !logbookId, refetchOnMountOrArgChange: true }
  );

  const app = logbook?.application;

  // default to current position week if available
  useEffect(() => {
    if (!logbook) return;
    const pos = Number(logbook?.position || 1);
    if (!Number.isNaN(pos) && pos >= 1) setSelectedWeek(pos);
  }, [logbook?.id]);

  const {
    data: weekData,
    isFetching: weekLoading,
    error: weekError,
    refetch: refetchWeek,
  } = useGetSupervisorLogbookWeekEntriesQuery(
    { logbook_id: logbookId || "", week: selectedWeek },
    { skip: !logbookId, refetchOnMountOrArgChange: true }
  );

  const entries = useMemo(() => normalizeEntries(weekData), [weekData]);

  // Map entries by day name
  const byDay = useMemo(() => {
    const map = new Map<Day, any>();
    for (const e of entries) {
      const dayName = NUM_TO_DAY?.[e.day] as Day | undefined;
      if (dayName) map.set(dayName, e);
    }
    return map;
  }, [entries]);

  /* ---------------- rules ---------------- */

  const logbookStatus = String(logbook?.status || "").toLowerCase();

  // ✅ only allow any action if in_progress OR accepted
  const canEdit =
    logbookStatus === "in_progress" || logbookStatus === "accepted";

  // ✅ submit for assessment when in_progress OR accepted
  const canSubmitForAssessment =
    logbookStatus === "in_progress" || logbookStatus === "accepted";

  const [approve, { isLoading: approving }] = useApproveDailyLogMutation();
  const [approveWeek, { isLoading: approvingWeek }] = useApproveLogbookWeekMutation();

  /**
   * A supervisor endorses progress, not individual days — clause 4.14 asks
   * them to sign at least once every three months, so five clicks a week for a
   * year was the wrong unit of work.
   *
   * The week button is an ADDITION, not a replacement. It approves whatever is
   * waiting in this week, however few days that is; the per-day buttons stay
   * exactly where they were, for the days written afterwards and for anyone
   * who would rather go one at a time. A half-filled week is the normal state
   * of a book being kept as the work happens.
   */
  const filledDays = useMemo(
    () => DAYS.filter((d: Day) => Boolean(byDay.get(d)?.id)).length,
    [byDay],
  );
  const unwrittenDays = DAYS.length - filledDays;

  const pendingInWeek = useMemo(
    () =>
      entries.filter((e: any) => {
        const status = String(e?.status || "pending").toLowerCase();
        return !e?.locked && status === "pending";
      }).length,
    [entries],
  );
  const [sendForAssessment, { isLoading: sendingAssessment }] =
    useSendLogbookForAssessmentMutation();
  const [unsubmitLogbook, { isLoading: unsubmitting }] =
    useUnsubmitLogbookMutation();

  const busy =
    lbLoading || weekLoading || approving || approvingWeek || sendingAssessment || unsubmitting;

  // Whole-logbook flags for the calendar. The week endpoint only ever returns
  // the week you are looking at, so without this the other 51 squares have
  // nothing to shade with and an outstanding review stays invisible until you
  // happen to click its week.
  const { data: weeksStatus } = useGetLogbookWeeksStatusQuery(
    { logbookId: logbookId || "" },
    { skip: !logbookId },
  );

  const weeksWithEntries = useMemo(
    () =>
      new Set<number>(
        Array.isArray(weeksStatus?.weeks_with_entries)
          ? weeksStatus.weeks_with_entries.map(Number)
          : entries.length
          ? [selectedWeek]
          : [],
      ),
    [weeksStatus, entries, selectedWeek],
  );

  const weeksWithOpenReview = useMemo(
    () =>
      new Set<number>(
        Array.isArray(weeksStatus?.weeks_flagged_for_you)
          ? weeksStatus.weeks_flagged_for_you.map(Number)
          : [],
      ),
    [weeksStatus],
  );

  /* ---------------- actions ---------------- */

  const doApprove = async (entry: any) => {
    const entryId = entry?.id;
    if (!entryId) return;

    if (!canEdit) {
      setModal({
        open: true,
        title: "View Only",
        message:
          "This logbook is no longer editable (not in_progress/accepted).",
      });
      return;
    }

    const status = String(entry?.status || "pending").toLowerCase();
    const locked = Boolean(entry?.locked) || status !== "pending";

    if (locked) {
      setModal({
        open: true,
        title: "Not Allowed",
        message: "This entry has already been decided/locked.",
      });
      return;
    }

    try {
      const res: any = await approve({ id: entryId }).unwrap();
      setModal({
        open: true,
        title: "Approved",
        message: res?.message || "Daily log approved.",
      });
      await refetchWeek();
      await refetchLogbook();
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  const doApproveWeek = async () => {
    if (!logbookId) return;

    try {
      const res: any = await approveWeek({
        logbookId,
        week: selectedWeek,
      }).unwrap();

      setModal({
        open: true,
        title: "Week Approved",
        message: res?.message || `Week ${selectedWeek} approved.`,
      });
      await refetchWeek();
      await refetchLogbook();
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  const doSendForAssessment = async () => {
    if (!logbook?.id) return;

    if (!canSubmitForAssessment) {
      setModal({
        open: true,
        title: "Not Allowed",
        message: "Logbook can only be submitted when status is in_progress or accepted.",
      });
      return;
    }

    try {
      const res: any = await sendForAssessment({ id: logbook.id }).unwrap();
      setAssessmentConfirmOpen(false);

      setModal({
        open: true,
        title: "Submitted",
        message: res?.message || "Logbook submitted for assessment.",
      });

      await refetchLogbook();
    } catch (e: any) {
      setAssessmentConfirmOpen(false);
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  const doUnsubmit = async () => {
    if (!logbook?.id) return;
    try {
      const res: any = await unsubmitLogbook({ logbookId: logbook.id }).unwrap();
      setModal({
        open: true,
        title: "Unsubmitted",
        message: res?.message || "Logbook reverted to in-progress.",
      });
      await refetchLogbook();
    } catch (e: any) {
      setModal({ open: true, title: "Error", message: safeMsg(e) });
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Top nav (same style as your other screen) */}
      <div className="mb-4 flex items-center justify-between">
        <button
          className="text-xs border px-3 py-1 rounded bg-white"
          onClick={() => navigate("/supervisor")}
          disabled={busy}
        >
          ← Back to Supervisor
        </button>

        <button
          className="text-xs border px-3 py-1 rounded bg-white"
          onClick={() => {
            refetchLogbook();
            refetchWeek();
          }}
          disabled={busy}
        >
          {busy ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {lbLoading && <p className="text-sm">Loading...</p>}

      {(lbError || weekError) &&
        (isNetworkOrAbort(lbError) || isNetworkOrAbort(weekError) ? (
          <div className="bg-white p-6 rounded-xl border space-y-3">
            <p className="text-sm font-semibold text-amber-700">Connection interrupted</p>
            <p className="text-sm text-gray-600">
              Your network dropped while loading this logbook — this is not an access
              problem. Please check your connection and try again.
            </p>
            <button
              onClick={() => {
                refetchLogbook();
                refetchWeek();
              }}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 disabled:opacity-50"
            >
              {busy ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-xl border">
            <p className="text-sm text-gray-700">
              Logbook not found or you do not have access to supervise it.
            </p>
          </div>
        ))}

      {/* Only a genuine empty result (successful request, no logbook) — not an error. */}
      {!lbLoading && !lbError && !weekError && !logbook && (
        <div className="bg-white p-6 rounded-xl border">
          <p className="text-sm text-gray-700">
            Logbook not found or you do not have access to supervise it.
          </p>
        </div>
      )}

      {logbook && (
        <div className="flex gap-6">
          {/* LEFT (same look as your LogbookDetails left panel) */}
          <div className="w-80 space-y-4">
            <div className="bg-indigo-50 p-5 rounded-xl border">
              <BookOpen size={24} className="text-indigo-600" />
              <h2 className="text-2xl font-black mt-2">
                Stage {logbook?.stage || app?.stage || 1}
              </h2>

              <div className="mt-2">
                <StatusBadge status={logbook?.status || "—"} />
              </div>

              <div className="mt-3 text-xs text-gray-700">
                <div className="font-semibold">
                  {logbook?.diet?.title || "—"}
                </div>
                <div>
                  {safeDate(logbook?.diet?.start_date)} →{" "}
                  {safeDate(logbook?.diet?.end_date)}
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-700">
                Progress:{" "}
                <span className="font-semibold">
                  {Number(logbook?.position || 0)} /{" "}
                  {Number(logbook?.size || 0)}
                </span>
              </div>

              <div className="mt-3 text-xs text-gray-700">
                Supervisee:{" "}
                <span className="font-semibold">
                  {personNameFromApplication(app)}
                </span>
              </div>

              {/* ✅ View-only notice */}
              {!canEdit && (
                <div className="mt-3 text-xs p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-900">
                  This logbook is <b>view-only</b> because the status is{" "}
                  <b>{logbookStatus || "—"}</b>. You can still change weeks and
                  review entries, but you cannot approve or submit.
                </div>
              )}

              {/* ✅ Send for assessment button + confirmation */}
              <div className="mt-4">
                <button
                  className="w-full text-xs px-3 py-2 rounded bg-black text-white disabled:opacity-50"
                  disabled={!canSubmitForAssessment || busy}
                  onClick={() => setAssessmentConfirmOpen(true)}
                  title={
                    canSubmitForAssessment
                      ? "Send logbook for assessment"
                      : "Only allowed when status is in_progress or accepted"
                  }
                >
                  {sendingAssessment ? "Sending..." : "Send for Assessment"}
                </button>

                {!canSubmitForAssessment && (
                  <p className="mt-2 text-[11px] text-gray-600">
                    Send for assessment is only available when logbook status is{" "}
                    <b>in_progress</b> or <b>accepted</b>.
                  </p>
                )}

                {/* ✅ Unsubmit — only while the logbook is submitted */}
                {logbookStatus === "submitted" && (
                  <div className="mt-3">
                    <button
                      className="w-full text-xs px-3 py-2 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                      disabled={busy}
                      onClick={doUnsubmit}
                      title="Revert this logbook to in-progress so it can be edited and resubmitted"
                    >
                      {unsubmitting ? "Unsubmitting..." : "Unsubmit Logbook"}
                    </button>
                    <p className="mt-2 text-[11px] text-gray-600">
                      Reverts the logbook to <b>in_progress</b> so it can be edited and
                      resubmitted.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Logbook Report (uploaded by the probationer) */}
            <div className="bg-white p-4 rounded-xl border">
              <div className="flex items-center gap-2 mb-2">
                <FileText size={16} className="text-indigo-600" />
                <h4 className="font-bold text-sm">Logbook Report</h4>
              </div>
              {logbook?.report_url ? (
                <a
                  href={logbook.report_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline p-2 rounded-lg bg-emerald-50 border border-emerald-100"
                >
                  <CheckCircle2 size={14} className="shrink-0" />
                  <span className="truncate">
                    View report{logbook.report_pages ? ` (${logbook.report_pages} pages)` : ""}
                  </span>
                </a>
              ) : (
                <p className="text-[11px] text-gray-400 italic">
                  No report uploaded by the probationer yet.
                </p>
              )}
            </div>

            {/* Week selector (NO disabled prop, week switching always works) */}
            <div className="bg-white p-4 rounded-xl border">
              <h4 className="font-bold text-sm">Select Week</h4>
              <WeekDropdown
                value={selectedWeek}
                onChange={(w: number) => setSelectedWeek(w)} // ✅ always allow moving weeks
                weeks={Number(logbook?.size || 0) || 1}
              />
              {weekLoading && (
                <p className="text-xs text-gray-500 mt-2">Loading week…</p>
              )}
            </div>

            <WeekCalendar
              totalWeeks={Number(logbook?.size || 0)}
              value={selectedWeek}
              onChange={(w: number) => setSelectedWeek(w)} // ✅ always allow
              loading={weekLoading}
              weeksWithEntries={weeksWithEntries as any}
              weeksWithOpenReview={weeksWithOpenReview as any}
              reviewNote="the candidate has answered your review"
            />
          </div>

          {/* RIGHT (same as your “Application Details + Entries”) */}
          <div className="flex-1 space-y-6">
            {/* Application details */}
            <div className="bg-white p-5 rounded-xl border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold">Application Details</h3>
                  <p className="text-xs text-gray-500">
                    Review daily entries and approve when allowed.
                  </p>
                </div>
                <div className="text-xs text-gray-500">
                  Week <b>{selectedWeek}</b>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Name</div>
                  <div className="font-medium">
                    {personNameFromApplication(app)}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="font-medium">{app?.email || "—"}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="font-medium">{app?.phone || "—"}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">
                    Application Status
                  </div>
                  <div className="font-medium">{app?.status || "—"}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Diet</div>
                  <div className="font-medium">
                    {logbook?.diet?.title || "—"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Logbook Status</div>
                  <div className="font-medium">{logbook?.status || "—"}</div>
                </div>
              </div>
            </div>

            {/* Daily entries list (same look as your other screen) */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold">
                    Week {selectedWeek} Entries
                  </h3>
                  <p className="text-xs text-gray-500">
                    {filledDays} of {DAYS.length} days written
                    {pendingInWeek > 0
                      ? ` · ${pendingInWeek} awaiting your approval`
                      : filledDays > 0
                      ? " · all decided"
                      : ""}
                    {unwrittenDays > 0
                      ? ` · ${unwrittenDays} still to be written`
                      : ""}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {approving && <p className="text-xs text-gray-500">Approving…</p>}

                  {/* Approves what is waiting, whatever the week holds. The
                      per-day buttons below stay regardless — this is a
                      shortcut, not a replacement. */}
                  {canEdit && (
                    <button
                      onClick={() => setWeekConfirmOpen(true)}
                      disabled={busy || pendingInWeek === 0}
                      className="bg-black text-white px-4 py-2 rounded text-xs disabled:opacity-50"
                    >
                      {pendingInWeek === 0
                        ? "Nothing to approve"
                        : approvingWeek
                        ? "Approving week…"
                        : `Approve ${pendingInWeek} ${
                            pendingInWeek === 1 ? "entry" : "entries"
                          } in this week`}
                    </button>
                  )}
                </div>
              </div>

              {DAYS.map((day: Day) => {
                const entry = byDay.get(day);
                const status = String(entry?.status || "pending").toLowerCase();
                const locked =
                  Boolean(entry?.locked) ||
                  (entry?.status && status !== "pending");

                const hasEntry = Boolean(entry?.id);

                // ✅ approve allowed only when canEdit and not locked
                const canApproveThis = canEdit && hasEntry && !locked && !busy;

                return (
                  <div
                    key={day}
                    className="bg-white p-4 rounded-xl border space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{day}</p>

                      {hasEntry ? (
                        <StatusBadge status={entry?.status || "pending"} />
                      ) : (
                        <span className="text-xs px-2 py-1 rounded border text-gray-600">
                          NO ENTRY
                        </span>
                      )}
                    </div>

                    {hasEntry ? (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
                        <span>
                          Entry date:{" "}
                          <span className="font-medium text-gray-800">
                            {safeDate(entry?.entry_date)}
                          </span>
                        </span>
                        <span>
                          Last updated:{" "}
                          <span className="font-medium text-gray-800">
                            {safeDate(entry?.updated_at)}
                          </span>
                        </span>
                        <span>
                          Hours:{" "}
                          <span className="font-medium text-gray-800">
                            {entry?.hours ?? "—"}
                          </span>
                        </span>
                      </div>
                    ) : null}

                    {/* The training code the day was logged under. Clause 2.2
                        requires one on every entry and the assessor reads the
                        book by it, so a supervisor reviewing the day has to
                        see whether the activity and the code actually match —
                        it was loaded by the API all along and simply never
                        rendered here. */}
                    {hasEntry ? (
                      entry?.training_code ? (
                        <div className="flex flex-wrap items-baseline gap-x-2 rounded border border-indigo-100 bg-indigo-50/60 px-2.5 py-1.5">
                          <span className="text-[11px] font-bold text-indigo-800">
                            {entry.training_code.code}
                          </span>
                          <span className="text-[11px] text-indigo-900">
                            {entry.training_code.description}
                          </span>
                        </div>
                      ) : (
                        <div className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                          No training code on this entry — every diary entry must
                          carry one (clause 2.2).
                        </div>
                      )
                    ) : null}

                    {/* Activity */}
                    <div className="text-sm text-gray-800 whitespace-pre-wrap border rounded p-3 bg-gray-50">
                      {entry?.activity || (
                        <span className="text-gray-500">
                          No entry submitted for {day}.
                        </span>
                      )}
                    </div>

                    {/* Approve row — always available. The week button is a
                        shortcut over these, not a replacement for them: a day
                        written after the week was approved is approved here. */}
                    <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                      <div className="text-xs text-gray-500 w-full">
                        {hasEntry ? (
                          !canEdit ? (
                            <span>
                              View-only mode (logbook status:{" "}
                              <b>{logbookStatus}</b>).
                            </span>
                          ) : locked ? (
                            <span>
                              This entry is already <b>{status}</b> and locked.
                            </span>
                          ) : (
                            <span>Click Approve to lock this daily log.</span>
                          )
                        ) : (
                          <span>Nothing to approve — {day} has not been written yet.</span>
                        )}
                      </div>

                      <button
                        onClick={() =>
                          setApproveConfirm({ open: true, entry, day })
                        }
                        disabled={!canApproveThis}
                        className="bg-black text-white px-4 py-2 rounded text-xs disabled:opacity-50 w-full md:w-auto"
                      >
                        {locked
                          ? "Approved"
                          : approving
                          ? "Approving..."
                          : "Approve"}
                      </button>
                    </div>

                    {/* Review is available on any day that exists, approved or
                        not — guidance on a day already signed off is still
                        worth writing, and the candidate carries it into the
                        next week. */}
                    {hasEntry && (
                      <EntryReviewBox
                        entry={entry}
                        disabled={!canEdit || busy}
                        onSaved={async () => {
                          await refetchWeek();
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-gray-500">
              <Link to="/supervisor" className="underline">
                Back to Supervisor Dashboard
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Confirm Approve */}
      <Modal
        open={approveConfirm.open}
        title="Approve Daily Log?"
        onClose={() => setApproveConfirm({ open: false })}
      >
        <div className="text-sm text-gray-700 space-y-3">
          <p>
            You are about to approve <b>{approveConfirm.day || "this day"}</b>.
            This will lock the entry and the user will be notified.
          </p>

          {!canEdit && (
            <div className="p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-900 text-xs">
              This logbook is currently <b>view-only</b>. Approving is not
              allowed.
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              className="px-3 py-2 rounded border text-xs disabled:opacity-50"
              onClick={() => setApproveConfirm({ open: false })}
              disabled={approving}
            >
              Cancel
            </button>

            <button
              className="px-3 py-2 rounded bg-black text-white text-xs disabled:opacity-50"
              onClick={() => {
                const entry = approveConfirm.entry;
                setApproveConfirm({ open: false });
                if (entry) doApprove(entry);
              }}
              disabled={approving || !canEdit}
            >
              {approving ? "Approving..." : "Yes, Approve"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ✅ Confirm Approve Week */}
      <Modal
        open={weekConfirmOpen}
        title={`Approve all of Week ${selectedWeek}?`}
        onClose={() => setWeekConfirmOpen(false)}
      >
        <div className="text-sm text-gray-700 space-y-3">
          <p>
            You are about to approve{" "}
            <b>
              {pendingInWeek} {pendingInWeek === 1 ? "entry" : "entries"}
            </b>{" "}
            in week {selectedWeek}. Each will be locked and can no longer be
            changed by the candidate.
          </p>

          {/* Said plainly, because a bulk action is where somebody worries
              about what it touches beyond what they meant. */}
          <p className="text-xs text-gray-500">
            Days you have already approved or rejected are left as they are.
            {unwrittenDays > 0 && (
              <>
                {" "}
                {unwrittenDays} {unwrittenDays === 1 ? "day has" : "days have"} not been written
                yet — approve {unwrittenDays === 1 ? "it" : "them"} individually once they are, or
                run this again.
              </>
            )}
          </p>

          <div className="flex gap-2 justify-end">
            <button
              className="px-3 py-2 rounded border text-xs disabled:opacity-50"
              onClick={() => setWeekConfirmOpen(false)}
              disabled={approvingWeek}
            >
              Cancel
            </button>

            <button
              className="px-3 py-2 rounded bg-black text-white text-xs disabled:opacity-50"
              onClick={() => {
                setWeekConfirmOpen(false);
                doApproveWeek();
              }}
              disabled={approvingWeek || !canEdit}
            >
              {approvingWeek ? "Approving..." : "Yes, Approve Week"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ✅ Confirm Send for Assessment */}
      <Modal
        open={assessmentConfirmOpen}
        title="Send Logbook for Assessment?"
        onClose={() => setAssessmentConfirmOpen(false)}
      >
        <div className="text-sm text-gray-700 space-y-3">
          <p>
            You are about to submit this logbook for assessment. Once submitted,
            an assessor will be assigned (if available), and this logbook may no
            longer be editable.
          </p>

          {!canSubmitForAssessment && (
            <div className="p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-900 text-xs">
              This action is only allowed when logbook status is{" "}
              <b>in_progress</b>.
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              className="px-3 py-2 rounded border text-xs disabled:opacity-50"
              onClick={() => setAssessmentConfirmOpen(false)}
              disabled={sendingAssessment}
            >
              Cancel
            </button>

            <button
              className="px-3 py-2 rounded bg-black text-white text-xs disabled:opacity-50"
              onClick={doSendForAssessment}
              disabled={sendingAssessment || !canSubmitForAssessment}
            >
              {sendingAssessment ? "Sending..." : "Yes, Send"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Info modal */}
      <Modal
        open={modal.open}
        title={modal.title}
        onClose={() => setModal({ open: false, title: "", message: "" })}
      >
        {modal.message}
      </Modal>

      {/* The other end of the candidate's thread — same widget, same
          conversation; the server decides which party is calling. */}
      <LogbookChat logbookId={logbookId} />
    </div>
  );
}
